import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { profileKeys, useMyWorker } from '@/hooks/useProfile';
import type { WorkerProfileEdits } from '@/hooks/useProfile';
import { useSkillsAndRoles, useRoleSkillIds } from '@/hooks/useSkillsAndRoles';
import { useAuthStore } from '@/stores/authStore';
import {
  completeWorkerCommand,
  pendingWorkerCommand,
  WORKER_COMMAND_CHANGED,
} from '@/services/workerRegistration';
import { weeklyMinutes } from '@/lib/auth-validation';
import { getWorkers } from '@/api/adapter/onboarding';
import { Button } from '@/components/ui/Button';
import type {
  CalendarDefinition,
  Qualifications,
  WorkerSummary,
  Proficiency,
  WorkerProfile,
} from '@/types/session';

function getRoleIcon(roleName: string): string {
  const lower = roleName.toLowerCase();
  if (lower.includes('front')) return 'ri-layout-masonry-line';
  if (lower.includes('back')) return 'ri-server-line';
  if (lower.includes('full')) return 'ri-stack-line';
  if (lower.includes('devops') || lower.includes('cloud'))
    return 'ri-cloud-line';
  if (lower.includes('qa') || lower.includes('test'))
    return 'ri-shield-check-line';
  if (lower.includes('design') || lower.includes('ui') || lower.includes('ux'))
    return 'ri-palette-line';
  if (lower.includes('architect')) return 'ri-building-4-line';
  if (
    lower.includes('smart') ||
    lower.includes('contract') ||
    lower.includes('solidity')
  )
    return 'ri-file-code-line';
  return 'ri-code-box-line';
}

type Kind = 'qualifications' | 'calendar';
type ProfileEditor = {
  value: WorkerProfileEdits;
  onChange: (value: WorkerProfileEdits) => void;
};
export function WorkerSettings({
  kind,
  embedded = false,
  roleExperiences,
  onExperienceChange,
  editor,
}: {
  kind: Kind;
  embedded?: boolean;
  roleExperiences?: WorkerProfile['roleExperiences'];
  onExperienceChange?: (roleId: number, proficiency: Proficiency) => void;
  editor?: ProfileEditor;
}) {
  const query = useMyWorker();
  const principal = useAuthStore((state) => state.session?.principalId);
  if (query.isPending) return <p role="status">Loading worker details…</p>;
  if (query.isError)
    return (
      <div role="alert">
        {query.error.message}{' '}
        <Button onClick={() => void query.refetch()}>
          Retry worker details
        </Button>
      </div>
    );
  if (!query.data || !principal)
    return (
      <p>
        Complete <Link to="/register/developer">developer onboarding</Link> to
        manage qualifications and availability.
      </p>
    );
  return (
    <WorkerEdit
      key={kind + ':' + principal}
      kind={kind}
      embedded={embedded}
      worker={query.data}
      principal={principal}
      roleExperiences={roleExperiences}
      onExperienceChange={onExperienceChange}
      editor={editor}
    />
  );
}
function WorkerEdit({
  kind,
  worker,
  principal,
  embedded,
  roleExperiences,
  onExperienceChange,
  editor,
}: {
  kind: Kind;
  worker: WorkerSummary;
  principal: string;
  embedded: boolean;
  roleExperiences?: WorkerProfile['roleExperiences'];
  onExperienceChange?: (roleId: number, proficiency: Proficiency) => void;
  editor?: ProfileEditor;
}) {
  const queryClient = useQueryClient();
  const catalog = useSkillsAndRoles();
  const [saved] = useState(() => {
    try {
      return { request: pendingWorkerCommand(principal, kind), error: '' };
    } catch (reason) {
      return { request: null, error: (reason as Error).message };
    }
  });
  const initialQualifications =
    kind === 'qualifications' && saved.request
      ? (saved.request.payload as { qualifications: Qualifications })
          .qualifications
      : worker.qualifications;
  const initialCalendar =
    kind === 'calendar' && saved.request
      ? (saved.request.payload as CalendarDefinition)
      : worker.calendar.definition;
  const [localRoleIds, setRoleIds] = useState(initialQualifications.roleIds);
  const [localSkillIds, setSkillIds] = useState(initialQualifications.skillIds);
  const roleIds = editor?.value.qualifications.roleIds ?? localRoleIds;
  const skillIds = editor?.value.qualifications.skillIds ?? localSkillIds;
  const selectedRoles = roleIds.filter((id) => id !== 1);
  const availableRoles = catalog.selectableRoles.filter(
    (role) => !roleIds.includes(role.id)
  );
  const associations = useRoleSkillIds(roleIds.filter((id) => id !== 1));
  const [localHours, setHours] = useState(
    String(initialCalendar.defaultWeeklyMinutes / 60)
  );
  const hours = editor?.value.hours ?? localHours;
  const [dirty, setDirty] = useState(false);
  const [pending, setPending] = useState(!!saved.request);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(saved.error);
  const [status, setStatus] = useState('');
  const controller = useRef<AbortController>();
  useEffect(() => () => controller.current?.abort(), []);
  useEffect(() => {
    if (editor || dirty || busy || pending) return;
    setRoleIds(worker.qualifications.roleIds);
    setSkillIds(worker.qualifications.skillIds);
    setHours(String(worker.calendar.definition.defaultWeeklyMinutes / 60));
  }, [worker, editor, dirty, busy, pending]);
  useEffect(() => {
    const refresh = (event: Event) => {
      const detail = (event as CustomEvent<{ principal: string; kind: string }>)
        .detail;
      if (detail.principal !== principal || detail.kind !== kind) return;
      try {
        setPending(!!pendingWorkerCommand(principal, kind));
      } catch (reason) {
        setError((reason as Error).message);
      }
    };
    window.addEventListener(WORKER_COMMAND_CHANGED, refresh);
    return () => window.removeEventListener(WORKER_COMMAND_CHANGED, refresh);
  }, [principal, kind]);
  const submit = async () => {
    setError('');
    setStatus('');
    setBusy(true);
    const current = new AbortController();
    controller.current = current;
    try {
      const existing = pendingWorkerCommand(principal, kind);
      let done: boolean;
      if (kind === 'calendar') {
        // Refresh before replacing the calendar so existing overrides are not erased.
        // The backend remains authoritative about reservations and concurrent updates.
        const minutes = existing
          ? 0
          : weeklyMinutes(hours.trim() === '' ? NaN : Number(hours));
        const latest = existing
          ? worker
          : (await getWorkers()).find(
              (entry) => entry.account === worker.account
            );
        if (!latest)
          throw new Error(
            'Worker details are unavailable. Reload before saving.'
          );
        done = await completeWorkerCommand(
          principal,
          'calendar',
          existing
            ? (existing.payload as CalendarDefinition)
            : {
                defaultWeeklyMinutes: minutes,
                overrides: latest.calendar.definition.overrides,
              },
          setStatus,
          current.signal
        );
      } else {
        if (!existing) {
          if (
            roleIds.some(
              (id) => !catalog.roles.some((role) => role.id === id)
            ) ||
            skillIds.some(
              (id) => !catalog.skills.some((skill) => skill.id === id)
            )
          )
            throw new Error(
              'An unavailable qualification is selected. Review the catalog before saving.'
            );
        }
        done = await completeWorkerCommand(
          principal,
          'qualifications',
          existing
            ? (existing.payload as { qualifications: Qualifications })
            : { qualifications: { roleIds, skillIds } },
          setStatus,
          current.signal
        );
      }
      if (current.signal.aborted) return;
      setPending(!done);
      if (done) {
        setDirty(false);
        setStatus('Changes confirmed.');
        await queryClient.invalidateQueries({
          queryKey: profileKeys.worker(principal),
        });
      }
    } catch (reason) {
      if (!current.signal.aborted) {
        setError(
          reason instanceof Error ? reason.message : 'Worker update failed.'
        );
        try {
          setPending(!!pendingWorkerCommand(principal, kind));
        } catch {
          setPending(true);
        }
      }
    } finally {
      if (!current.signal.aborted) setBusy(false);
    }
  };
  const Container = embedded ? 'section' : 'form';
  const toggleSkill = (id: number) => {
    if (editor) {
      editor.onChange({
        ...editor.value,
        qualifications: {
          ...editor.value.qualifications,
          skillIds: skillIds.includes(id)
            ? skillIds.filter((value) => value !== id)
            : [...skillIds, id],
        },
      });
      return;
    }
    setDirty(true);
    setSkillIds((current) =>
      current.includes(id)
        ? current.filter((value) => value !== id)
        : [...current, id]
    );
  };
  const roleSkills = new Set(Object.values(associations.data ?? {}).flat());
  const extraSkills = skillIds.filter((id) => !roleSkills.has(id));
  return (
    <Container
      className={
        embedded
          ? `space-y-4 text-left ${kind === 'qualifications' ? 'mt-6 pt-1' : ''}`
          : 'backendForm space-y-4 rounded-[--radi-6] border border-[--base-border] bg-[--base-surface-2] p-5 text-left shadow-elevation-xs sm:p-8'
      }
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      aria-label={
        kind === 'calendar' ? 'Worker availability' : 'Worker qualifications'
      }
    >
      {kind === 'calendar' ? (
        <h2 className="text-lg font-semibold">Weekly availability</h2>
      ) : (
        <div className="flex flex-col justify-between gap-1 border-b border-[--base-border] pb-2.5 sm:flex-row sm:items-center">
          <div>
            <h2 className="flex items-center gap-2 text-sm font-semibold tracking-wide text-white">
              <i className="ri-user-settings-line text-base text-emerald-400" />
              Roles & Skills
            </h2>
            <p className="mt-0.5 text-xs text-[--text-dark-tertiary]">
              Select your engineering roles, experience level, and competencies
              for each.
            </p>
          </div>
          {selectedRoles.length > 0 && (
            <span className="self-start rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-0.5 text-xs font-medium text-emerald-400 sm:self-auto">
              {selectedRoles.length}{' '}
              {selectedRoles.length === 1 ? 'Role' : 'Roles'}
            </span>
          )}
        </div>
      )}
      <fieldset
        disabled={busy || pending || !!saved.error}
        className="min-w-0 space-y-4"
      >
        {kind === 'calendar' ? (
          <>
            <label className="!flex !flex-col !items-stretch gap-2">
              Hours per week
              <input
                aria-label="Hours per week"
                className="input w-full"
                type="number"
                min="0"
                max="168"
                step="1"
                required
                value={hours}
                onChange={(event) => {
                  if (editor) {
                    editor.onChange({
                      ...editor.value,
                      hours: event.target.value,
                    });
                    return;
                  }
                  setDirty(true);
                  setHours(event.target.value);
                }}
              />
            </label>
            <p className="text-sm text-muted-foreground">
              Whole hours from 0 to 168. Zero means unavailable for weeks using
              the default capacity. Existing weekly overrides and reservations
              are preserved.
            </p>
            {worker.calendar.definition.overrides.length > 0 && (
              <p>
                {worker.calendar.definition.overrides.length} weekly override(s)
                remain unchanged.
              </p>
            )}
            {worker.calendar.committedMinutes.length > 0 && (
              <p>
                Existing commitments may prevent reducing capacity. The server
                checks them before confirming.
              </p>
            )}
          </>
        ) : (
          <>
            {catalog.isError && (
              <p role="alert">Cannot load roles and skills.</p>
            )}
            <div className="space-y-3">
              {selectedRoles.length === 0 && (
                <p className="text-xs text-amber-400">
                  Please select at least one role.
                </p>
              )}
              {selectedRoles.map((id) => (
                <div
                  key={id}
                  className="space-y-3.5 rounded-xl border border-[#2b2727] bg-[#161515] p-4 text-center shadow-sm transition-all hover:border-[#383333]"
                >
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#242121] pb-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-emerald-500/20 bg-emerald-500/10 text-emerald-400">
                        <i
                          className={`${getRoleIcon(catalog.getRoleName(id) ?? '')} text-lg`}
                        />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold capitalize tracking-wide text-white">
                          {catalog.getRoleName(id) ?? 'Unavailable role #' + id}
                        </h4>
                        <span className="mt-0.5 block text-[11px] text-[--text-dark-tertiary]">
                          {
                            (associations.data?.[id] ?? []).filter((skillId) =>
                              skillIds.includes(skillId)
                            ).length
                          }{' '}
                          of {(associations.data?.[id] ?? []).length} skills
                          selected
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2.5">
                      {onExperienceChange && (
                        <div
                          className="flex w-fit items-center rounded-lg border border-[#2c2828] bg-[#100f0f] p-0.5"
                          role="group"
                          aria-label={`Experience for ${catalog.getRoleName(id) ?? 'role #' + id}`}
                        >
                          {(['junior', 'mid-level', 'senior'] as const).map(
                            (level) => {
                              const active =
                                roleExperiences?.find(
                                  (entry) => entry.roleId === id
                                )?.proficiency === level;
                              return (
                                <button
                                  key={level}
                                  type="button"
                                  aria-pressed={active}
                                  onClick={() => onExperienceChange(id, level)}
                                  className={`cursor-pointer rounded-md border px-2.5 py-1 text-xs font-medium capitalize transition-all ${
                                    active
                                      ? 'border-emerald-500/30 bg-emerald-500/20 font-semibold text-emerald-400 shadow-sm'
                                      : 'border-transparent text-[#8c8c8c] hover:text-white'
                                  }`}
                                >
                                  {level === 'mid-level' ? 'Mid' : level}
                                </button>
                              );
                            }
                          )}
                        </div>
                      )}
                      {selectedRoles.length > 1 && (
                        <button
                          type="button"
                          aria-label={
                            'Remove ' +
                            (catalog.getRoleName(id) ?? 'role #' + id)
                          }
                          onClick={() => {
                            if (editor) {
                              editor.onChange({
                                ...editor.value,
                                qualifications: {
                                  ...editor.value.qualifications,
                                  roleIds: roleIds.filter(
                                    (value) => value !== id
                                  ),
                                },
                              });
                              return;
                            }
                            setDirty(true);
                            setRoleIds(roleIds.filter((value) => value !== id));
                          }}
                          className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-lg border border-transparent text-[#737373] transition-all hover:border-red-500/20 hover:bg-red-500/10 hover:text-red-400"
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="mb-2">
                      <span className="text-xs font-medium text-[#a3a3a3]">
                        Skills for {catalog.getRoleName(id) ?? 'this role'}:
                      </span>
                    </div>
                    {(associations.data?.[id] ?? []).length === 0 && (
                      <div className="rounded-lg border border-[#242121] bg-[#121111] px-3.5 py-3 text-xs italic text-[#737373]">
                        {associations.isLoading
                          ? 'Loading skills catalog…'
                          : associations.isError
                            ? 'Cannot load role skills.'
                            : 'No skills found for this role.'}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {(associations.data?.[id] ?? []).map((skillId) => (
                        <button
                          type="button"
                          key={skillId}
                          aria-pressed={skillIds.includes(skillId)}
                          onClick={() => toggleSkill(skillId)}
                          className={
                            'group inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-all ' +
                            (skillIds.includes(skillId)
                              ? 'border-emerald-500/40 bg-emerald-500/15 font-semibold text-emerald-400 shadow-[0_0_12px_rgba(54,211,153,0.08)]'
                              : 'border-[#292525] bg-[#131111] text-[#9b9b9b] hover:border-[#3d3838] hover:text-white')
                          }
                        >
                          <span
                            className={`flex h-3.5 w-3.5 items-center justify-center rounded text-[10px] transition-colors ${skillIds.includes(skillId) ? 'bg-emerald-400 text-black' : 'border border-[#454040] group-hover:border-[#605b5b]'}`}
                          >
                            {skillIds.includes(skillId) && (
                              <i className="ri-check-line font-bold" />
                            )}
                          </span>
                          {catalog.getSkillName(skillId) ?? 'Skill #' + skillId}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
              {extraSkills.length > 0 && (
                <div>
                  <p className="mb-2 text-xs text-[#a3a3a3]">
                    Other selected skills
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {extraSkills.map((id) => (
                      <button
                        type="button"
                        key={id}
                        aria-pressed={true}
                        onClick={() => toggleSkill(id)}
                        className="rounded-lg border border-emerald-500/40 bg-emerald-500/15 px-3 py-1.5 text-xs text-emerald-400"
                      >
                        {catalog.getSkillName(id) ?? 'Unavailable skill #' + id}{' '}
                        ×
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {availableRoles.length > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-[#2d2929] bg-[#141212]/70 p-3.5">
                  <div className="flex items-center gap-2">
                    <div className="flex h-6 w-6 items-center justify-center rounded-md bg-emerald-500/10 text-emerald-400">
                      <i className="ri-add-line text-sm" />
                    </div>
                    <span className="text-xs font-medium text-[#a3a3a3]">
                      Add more roles to your profile:
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {availableRoles.map((role) => (
                      <button
                        type="button"
                        key={role.id}
                        aria-label={'Add ' + role.name}
                        disabled={catalog.isLoading || catalog.isError}
                        onClick={() => {
                          onExperienceChange?.(role.id, 'junior');
                          if (editor) {
                            editor.onChange({
                              ...editor.value,
                              qualifications: {
                                ...editor.value.qualifications,
                                roleIds: [...roleIds, role.id],
                              },
                            });
                            return;
                          }
                          setDirty(true);
                          setRoleIds([...roleIds, role.id]);
                        }}
                        className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-[#302c2c] bg-[#191717] px-3 py-1 text-xs font-medium capitalize text-[#cfcfcf] transition-all hover:border-emerald-500/50 hover:bg-emerald-500/10 hover:text-emerald-400"
                      >
                        <i
                          className={`${getRoleIcon(role.name)} text-xs opacity-70`}
                        />{' '}
                        {role.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                {onExperienceChange && !editor
                  ? 'Experience is saved with the profile. Save qualifications updates roles and skills. '
                  : ''}
                Coordinator eligibility is managed separately.
              </p>
              {associations.isError && (
                <p role="alert">
                  Cannot load role skills.{' '}
                  <Button
                    type="button"
                    onClick={() => void associations.refetch()}
                  >
                    Retry skills
                  </Button>
                </p>
              )}
            </div>
          </>
        )}
      </fieldset>
      {pending && !editor && (
        <p>
          An earlier update is pending. Checking confirms that saved request;
          edited values are not submitted until it finishes.
        </p>
      )}
      {error && <p role="alert">{error}</p>}
      {status && <p role="status">{status}</p>}
      {!editor && (
        <Button
          type={embedded ? 'button' : 'submit'}
          onClick={embedded ? () => void submit() : undefined}
          disabled={
            busy ||
            !!saved.error ||
            (kind === 'qualifications' &&
              !pending &&
              (catalog.isLoading || catalog.isError))
          }
        >
          {busy
            ? 'Waiting for confirmation…'
            : pending
              ? 'Check pending update'
              : kind === 'calendar'
                ? 'Save availability'
                : 'Save qualifications'}
        </Button>
      )}
      {catalog.isError && kind === 'qualifications' && (
        <Button type="button" onClick={() => void catalog.rolesQuery.refetch()}>
          Retry catalog
        </Button>
      )}
    </Container>
  );
}
